import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { formatINR, formatDate } from "@/lib/utils.helpers";
import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Download, FileText, ArrowLeft } from "lucide-react";

export default function SalarySlip() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const cycle = search.get("cycle");
  const [data, setData] = useState(null);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    (async () => {
      const [d, s] = await Promise.all([
        api.get(`/payroll/employee/${id}${cycle ? `?cycle=${cycle}` : ""}`),
        api.get("/settings"),
      ]);
      setData(d.data);
      setCompany(s.data);
    })();
  }, [id, cycle]);

  const downloadPdf = async () => {
    const url = `${BACKEND_URL}/api/reports/salary-slip/${id}${cycle ? `?cycle=${cycle}` : ""}`;
    const token = localStorage.getItem("token");
    const res = await fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `salary_slip_${data?.employee?.name?.replace(/\s/g, "_") || "employee"}.pdf`;
    link.click();
  };

  if (!data || !company) return <div className="text-slate-500">Loading...</div>;
  const e = data.employee;
  const p = data.payroll;

  return (
    <div className="max-w-4xl space-y-6" data-testid="salary-slip-page">
      <div className="flex justify-between items-center no-print">
        <Link to="/admin/employees" className="text-sm text-slate-600 hover:text-[#C59B27] flex items-center">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Link>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="print-slip"><FileText className="w-4 h-4 mr-1" /> Print</Button>
          <Button onClick={downloadPdf} data-testid="download-slip-pdf" className="bg-[#111111] hover:bg-[#C59B27] transition-colors text-white">
            <Download className="w-4 h-4 mr-1" /> Download PDF
          </Button>
        </div>
      </div>

      <Card className="border-[#E5E2DC] p-8 bg-white">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-[#C59B27] pb-4">
          <div className="flex items-start gap-4">
            <BrandLogo alt="Logo" className="w-16 h-16 object-contain rounded" />
            <div>
              <div className="font-serif text-2xl font-semibold text-slate-900">{company.name}</div>
              <div className="text-sm text-slate-600">{company.address}</div>
              {company.phone && <div className="text-xs text-slate-500">Ph: {company.phone}</div>}
            </div>
          </div>
          <div className="text-right">
            <div className="font-serif text-2xl font-semibold text-slate-900">Salary Slip</div>
            <div className="text-xs uppercase tracking-wider text-[#C59B27] font-bold mt-1">Period: {data.label}</div>
            <div className="text-xs text-slate-500 mt-1">Generated: {new Date().toLocaleDateString("en-IN")}</div>
          </div>
        </div>

        {/* Employee Info */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-3 mt-6 pb-6 border-b border-[#E5E2DC]">
          <div className="flex items-center gap-4">
            {e.photo_url ? (
              <img src={`${BACKEND_URL}${e.photo_url}`} alt="" className="w-16 h-16 rounded object-cover bg-slate-100" />
            ) : (
              <div className="w-16 h-16 rounded bg-[#C59B27]/20 flex items-center justify-center text-[#C59B27] text-2xl font-bold">
                {e.name?.[0]?.toUpperCase()}
              </div>
            )}
            <div>
              <div className="font-serif text-lg font-semibold">{e.name}</div>
              <div className="text-xs text-slate-500">ID: {e.id.slice(0, 8).toUpperCase()}</div>
            </div>
          </div>
          <InfoRow label="Employee Code" value={e.employee_code || "—"} />
          <InfoRow label="Father's Name" value={e.father_name || "—"} />
          <InfoRow label="Department" value={e.department || "—"} />
          <InfoRow label="Joining Date" value={formatDate(e.joining_date)} />
          <InfoRow label="Mobile" value={e.mobile || "—"} />
          <InfoRow label="PAN" value={e.pan || "—"} />
          <InfoRow label="Aadhaar" value={e.aadhaar || "—"} />
          <InfoRow label="Bank" value={`${e.bank_name || "—"} / ${e.account_number || "—"}`} />
          <InfoRow label="IFSC" value={e.ifsc || "—"} />
        </div>

        {/* Attendance */}
        <div className="mt-6">
          <h3 className="font-serif text-lg text-slate-900 mb-3">Attendance</h3>
          <div className="grid grid-cols-5 gap-3">
            <AttBox label="Present" value={p.present} color="text-green-700" />
            <AttBox label="Half Day" value={p.half_day} color="text-yellow-700" />
            <AttBox label="Absent" value={p.absent} color="text-red-700" />
            <AttBox label="Leave" value={p.leave} color="text-blue-700" />
            <AttBox label="OT (hrs)" value={p.overtime_hours} color="text-[#C59B27]" />
          </div>
        </div>

        {/* Earnings */}
        <div className="mt-6">
          <h3 className="font-serif text-lg text-slate-900 mb-3">Earnings & Deductions</h3>
          <div className="border border-[#E5E2DC] rounded overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                <Row label={`Earned Salary (${p.effective_days} days × ${formatINR(p.per_day)})`} value={p.earned} />
                <Row label={`Overtime (${p.overtime_hours} hrs)`} value={p.overtime_amount} />
                <Row label="Bonus" value={p.bonus} />
                <Row label="Gross" value={p.gross} bold highlight />
                <Row label="Advances" value={p.advances} negative />
                <Row label="Deductions" value={p.deductions} negative />
                <Row label="Net Payable" value={p.net_payable} bold highlight="green" />
                <Row label="Payments Made" value={p.payments_made} />
                <Row label="Remaining Balance" value={p.remaining_balance} bold />
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer / Signature */}
        <div className="flex items-end justify-between mt-16 pt-4 border-t border-[#E5E2DC]">
          <div className="text-xs text-slate-500">
            <div className="font-semibold text-[#0F1D42]">{company.name}</div>
            <div>{company.address}</div>
          </div>
          <div className="text-right">
            {company.signature_url && (
              <img src={`${BACKEND_URL}${company.signature_url}`} alt="Signature" className="h-14 mb-1 ml-auto" />
            )}
            <div className="border-t-2 border-slate-800 pt-2 min-w-[220px]">
              <div className="font-semibold text-slate-900">{company.signatory_name}</div>
              <div className="text-xs text-slate-500">{company.signatory_title}</div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

const InfoRow = ({ label, value }) => (
  <div className="text-sm">
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    <div className="text-slate-900 mt-0.5">{value}</div>
  </div>
);

const AttBox = ({ label, value, color }) => (
  <div className="border border-[#E5E2DC] rounded p-3 text-center bg-[#FDFDF9]">
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    <div className={`font-serif text-xl mt-1 ${color}`}>{value}</div>
  </div>
);

const Row = ({ label, value, bold, negative, highlight }) => (
  <tr className={
    `border-b border-[#E5E2DC] last:border-0 ` +
    (highlight === "green" ? "bg-green-50 " : highlight ? "bg-[#FEF3C7] " : "")
  }>
    <td className={`px-3 py-2 ${bold ? "font-semibold" : ""}`}>{label}</td>
    <td className={`px-3 py-2 text-right ${bold ? "font-semibold" : ""} ${negative ? "text-red-700" : ""}`}>
      {negative && value > 0 ? "− " : ""}{formatINR(value)}
    </td>
  </tr>
);
